<div class="flex h-full min-h-0 w-full flex-col overflow-hidden">
  <div class="shrink-0 space-y-2 border-b border-zinc-200 p-4 dark:border-zinc-800">
    <h4 class="text-sm font-semibold text-primary">Creator stats</h4>
    <p class="text-xs text-secondary">
      Search impressions, profile visits, brief invites, and hires per creator. Sorted by search
      appearances by default. Requires GodMode, Account Manager, or Creator Admin.
    </p>
    <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <label class="grid w-full max-w-md grid-cols-1 sm:max-w-sm">
        <span class="sr-only">Search creators</span>
        <input
          type="search"
          class="input-icon col-start-1 row-start-1 text-primary"
          placeholder="Search by creator ID, name, or email"
          [value]="searchQuery()"
          (input)="onSearchInput($event)"
        />
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="pointer-events-none col-start-1 row-start-1 ml-3 size-5 self-center text-secondary sm:size-4"
          aria-hidden="true"
        >
          <path d="m21 21-4.34-4.34" />
          <circle cx="11" cy="11" r="8" />
        </svg>
      </label>
      <p class="m-0 shrink-0 text-sm font-medium tabular-nums text-secondary" aria-live="polite">
        {{ recordCountLabel }}
      </p>
    </div>
  </div>

  @if (loadingState === 'error' && error) {
  <div class="shrink-0 mx-4 mt-4 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900/50 dark:bg-red-950/40">
    <p class="m-0 text-sm text-red-700 dark:text-red-300">{{ error }}</p>
  </div>
  }

  <div class="flex min-h-0 flex-1 flex-col overflow-hidden bg-white dark:bg-zinc-950">
    <div
      class="min-h-0 flex-1 overflow-auto scrollbar-transparent"
      #scrollDiv
      infinite-scroll
      [infiniteScrollDistance]="scrollDistance"
      [infiniteScrollUpDistance]="scrollUpDistance"
      [infiniteScrollThrottle]="throttle"
      [infiniteScrollContainer]="scrollDiv"
      (scrolled)="onScrollDown()"
    >
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200 text-sm dark:divide-zinc-700">
          <thead class="sticky top-0 z-10 bg-gray-50 dark:bg-zinc-800/80">
            <tr>
              <th scope="col" class="px-4 py-3 text-left font-semibold text-gray-700 dark:text-zinc-300">
                <button
                  type="button"
                  class="inline-flex items-center gap-1 rounded hover:text-indigo-700 dark:hover:text-indigo-400"
                  [class.text-indigo-600]="isSortedBy('creator')"
                  [class.dark:text-indigo-400]="isSortedBy('creator')"
                  [class.text-gray-700]="!isSortedBy('creator')"
                  [class.dark:text-zinc-300]="!isSortedBy('creator')"
                  (click)="toggleSort('creator')"
                  [disabled]="isLoading || loadingMore"
                  aria-label="Sort by creator"
                >
                  <span>Creator</span>
                  @if (isSortedBy('creator')) {
                    @if (sortOrder === 'ASC') {
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="lucide lucide-chevron-up h-3 w-3"
                        aria-hidden="true"
                      >
                        <path d="m18 15-6-6-6 6" />
                      </svg>
                    } @else {
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="lucide lucide-chevron-down h-3 w-3"
                        aria-hidden="true"
                      >
                        <path d="m6 9 6 6 6-6" />
                      </svg>
                    }
                  }
                </button>
              </th>
              <th scope="col" class="px-4 py-3 text-right font-semibold text-gray-700 dark:text-zinc-300">
                <button
                  type="button"
                  class="inline-flex w-full items-center justify-end gap-1 rounded hover:text-indigo-700 dark:hover:text-indigo-400"
                  [class.text-indigo-600]="isSortedBy('appearedInSearches')"
                  [class.dark:text-indigo-400]="isSortedBy('appearedInSearches')"
                  [class.text-gray-700]="!isSortedBy('appearedInSearches')"
                  [class.dark:text-zinc-300]="!isSortedBy('appearedInSearches')"
                  (click)="toggleSort('appearedInSearches')"
                  [disabled]="isLoading || loadingMore"
                  aria-label="Sort by search appearances"
                >
                  <span>Search appearances</span>
                  @if (isSortedBy('appearedInSearches')) {
                    @if (sortOrder === 'ASC') {
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="lucide lucide-chevron-up h-3 w-3"
                        aria-hidden="true"
                      >
                        <path d="m18 15-6-6-6 6" />
                      </svg>
                    } @else {
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="lucide lucide-chevron-down h-3 w-3"
                        aria-hidden="true"
                      >
                        <path d="m6 9 6 6 6-6" />
                      </svg>
                    }
                  }
                </button>
              </th>
              <th scope="col" class="px-4 py-3 text-right font-semibold text-gray-700 dark:text-zinc-300">
                <button
                  type="button"
                  class="inline-flex w-full items-center justify-end gap-1 rounded hover:text-indigo-700 dark:hover:text-indigo-400"
                  [class.text-indigo-600]="isSortedBy('profileVisits')"
                  [class.dark:text-indigo-400]="isSortedBy('profileVisits')"
                  [class.text-gray-700]="!isSortedBy('profileVisits')"
                  [class.dark:text-zinc-300]="!isSortedBy('profileVisits')"
                  (click)="toggleSort('profileVisits')"
                  [disabled]="isLoading || loadingMore"
                  aria-label="Sort by profile visits"
                >
                  <span>Profile visits</span>
                  @if (isSortedBy('profileVisits')) {
                    @if (sortOrder === 'ASC') {
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="lucide lucide-chevron-up h-3 w-3"
                        aria-hidden="true"
                      >
                        <path d="m18 15-6-6-6 6" />
                      </svg>
                    } @else {
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="lucide lucide-chevron-down h-3 w-3"
                        aria-hidden="true"
                      >
                        <path d="m6 9 6 6 6-6" />
                      </svg>
                    }
                  }
                </button>
              </th>
              <th scope="col" class="px-4 py-3 text-right font-semibold text-gray-700 dark:text-zinc-300">
                <button
                  type="button"
                  class="inline-flex w-full items-center justify-end gap-1 rounded hover:text-indigo-700 dark:hover:text-indigo-400"
                  [class.text-indigo-600]="isSortedBy('invitedToBrief')"
                  [class.dark:text-indigo-400]="isSortedBy('invitedToBrief')"
                  [class.text-gray-700]="!isSortedBy('invitedToBrief')"
                  [class.dark:text-zinc-300]="!isSortedBy('invitedToBrief')"
                  (click)="toggleSort('invitedToBrief')"
                  [disabled]="isLoading || loadingMore"
                  aria-label="Sort by brief invites"
                >
                  <span>Invited</span>
                  @if (isSortedBy('invitedToBrief')) {
                    @if (sortOrder === 'ASC') {
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="lucide lucide-chevron-up h-3 w-3"
                        aria-hidden="true"
                      >
                        <path d="m18 15-6-6-6 6" />
                      </svg>
                    } @else {
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="lucide lucide-chevron-down h-3 w-3"
                        aria-hidden="true"
                      >
                        <path d="m6 9 6 6 6-6" />
                      </svg>
                    }
                  }
                </button>
              </th>
              <th scope="col" class="px-4 py-3 text-right font-semibold text-gray-700 dark:text-zinc-300">
                <button
                  type="button"
                  class="inline-flex w-full items-center justify-end gap-1 rounded hover:text-indigo-700 dark:hover:text-indigo-400"
                  [class.text-indigo-600]="isSortedBy('hiredForBrief')"
                  [class.dark:text-indigo-400]="isSortedBy('hiredForBrief')"
                  [class.text-gray-700]="!isSortedBy('hiredForBrief')"
                  [class.dark:text-zinc-300]="!isSortedBy('hiredForBrief')"
                  (click)="toggleSort('hiredForBrief')"
                  [disabled]="isLoading || loadingMore"
                  aria-label="Sort by hires"
                >
                  <span>Hired</span>
                  @if (isSortedBy('hiredForBrief')) {
                    @if (sortOrder === 'ASC') {
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="lucide lucide-chevron-up h-3 w-3"
                        aria-hidden="true"
                      >
                        <path d="m18 15-6-6-6 6" />
                      </svg>
                    } @else {
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="lucide lucide-chevron-down h-3 w-3"
                        aria-hidden="true"
                      >
                        <path d="m6 9 6 6 6-6" />
                      </svg>
                    }
                  }
                </button>
              </th>
              <th scope="col" class="px-4 py-3 text-right font-semibold text-gray-700 dark:text-zinc-300">
                Last updated
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 bg-white dark:divide-zinc-700 dark:bg-zinc-950">
            @if (isLoading && !hasRowData) {
              <tr>
                <td colspan="6" class="px-4 py-8">
                  <atom-loading-inline title="Loading creator stats" message="Fetching rows…" />
                </td>
              </tr>
            } @else if (hasRowData) {
              @for (row of rows; track row.creatorId) {
                <tr class="hover:bg-gray-50 dark:hover:bg-zinc-900/50">
                  <td class="px-4 py-3">
                    <div class="flex min-w-0 items-center gap-3">
                      <div
                        class="relative size-10 shrink-0 overflow-hidden rounded-full bg-gray-100 ring-1 ring-gray-200 dark:bg-zinc-800 dark:ring-zinc-700"
                      >
                        <atom-ldg-img
                          class="block size-10"
                          inputClass="object-cover"
                          h="10"
                          w="10"
                          radius="full"
                          [src]="imageService.getImageSource(row.photoURL, 'gallery')"
                        />
                      </div>
                      <div class="min-w-0">
                        <a
                          class="block truncate font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-300 dark:hover:text-indigo-200"
                          [routerLink]="['/admin-options/user-profile', row.creatorId]"
                        >
                          {{ row.firstName }} {{ row.lastName }}
                        </a>
                        <p class="m-0 truncate text-xs text-secondary">{{ row.email || row.creatorId }}</p>
                      </div>
                    </div>
                  </td>
                  <td class="px-4 py-3 text-right text-gray-900 dark:text-zinc-100">
                    {{ row.appearedInSearches }}
                  </td>
                  <td class="px-4 py-3 text-right text-gray-900 dark:text-zinc-100">
                    {{ row.profileVisits }}
                  </td>
                  <td class="px-4 py-3 text-right text-gray-900 dark:text-zinc-100">
                    {{ row.invitedToBrief }}
                  </td>
                  <td class="px-4 py-3 text-right text-gray-900 dark:text-zinc-100">
                    {{ row.hiredForBrief }}
                  </td>
                  <td class="px-4 py-3 text-right text-xs text-secondary">
                    {{ formatDate(row.updatedDate) }}
                  </td>
                </tr>
              }
              @if (loadingMore) {
                <tr>
                  <td colspan="6" class="px-4 py-4 text-center text-xs text-secondary">Loading more…</td>
                </tr>
              }
            } @else if (!isLoading) {
              <tr>
                <td colspan="6" class="px-4 py-8 text-center text-sm text-secondary">
                  No creator stats found.
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </div>
  </div>
</div>
